import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription, interval } from 'rxjs';
import {
  ApplicationDialogService,
  setupUntilDestroy,
} from '@portal-workspace/grow-ui-library';
import { PortalHotToastService } from '@portal-workspace/grow-ui-library';
import { loadingFor } from '@ngneat/loadoff';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BasiqService } from 'apps/portal2/src/app/service/basiq.service';
import { TwoFAVerificationDialogData, TwoFAVerificationDialogResult } from '@portal-workspace/grow-shared-library';

@UntilDestroy({ arrayName: 'subscriptions', checkProperties: true })
@Component({
  templateUrl: './2fa-verification.dialog.html',
  styleUrls: ['./2fa-verification.dialog.scss'],
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, MatCheckboxModule, MatTooltipModule, FlexModule, MatButtonModule, AsyncPipe, MatDialogModule]
})
export class TwoFAVerificationDialog implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];
  loader = loadingFor('verification');
  otpForm!: FormGroup;
  digitControls: FormControl[] = [];
  otpDeliveryMethod: 'sms' | 'email';
  authenticationValue: string;
  mobileLast4Digits: string;
  
  errorMessage: string = '';
  isVerifying: boolean = false;
  isResending: boolean = false;
  isBlocked: boolean = false;
  resendCooldown: number = 0;
  private cooldownSubscription?: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TwoFAVerificationDialogData,
    private dialogRef: MatDialogRef<TwoFAVerificationDialog, TwoFAVerificationDialogResult>,
    private formBuilder: FormBuilder,
    private basiqService: BasiqService,
    private applicationDialogService: ApplicationDialogService,
    private toastService: PortalHotToastService
  ) {
    // Create 6 separate form controls for each digit
    for (let i = 0; i < 6; i++) {
      const control = formBuilder.control('', [Validators.required, Validators.pattern('[0-9]')]);
      this.digitControls.push(control);
    }

    this.otpForm = formBuilder.group({
      digit0: this.digitControls[0],
      digit1: this.digitControls[1],
      digit2: this.digitControls[2],
      digit3: this.digitControls[3],
      digit4: this.digitControls[4],
      digit5: this.digitControls[5],
    });

    this.otpDeliveryMethod = data.otpDeliveryMethod;
    this.authenticationValue = data.authenticationValue;
    this.mobileLast4Digits = this.otpDeliveryMethod === 'sms' ? this.authenticationValue?.slice(-4) ?? '' : '';
  }

  ngOnInit() {
    // Start cooldown timer (60 seconds)
    this.startResendCooldown(60);
    setupUntilDestroy(this);
  }

  ngOnDestroy() {
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }
    this.startResendCooldown(0);
  }

  onDigitInput(event: any, index: number): void {
    const input = event.target;
    const value = input.value;

    // Only allow single digits
    if (value && !/^\d$/.test(value)) {
      input.value = '';
      this.digitControls[index].setValue('');
      return;
    }

    // Auto-advance to next input if digit entered
    if (value && index < 5) {
      const nextInput = document.querySelector(`#digit-${index + 1}`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onDigitKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.digitControls[index].value && index > 0) {
      // Move to previous input on backspace if current is empty
      const prevInput = document.querySelector(`#digit-${index - 1}`) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  onDigitPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData && /^\d{6}$/.test(pastedData)) {
      // If 6 digits are pasted, fill all inputs
      for (let i = 0; i < 6; i++) {
        this.digitControls[i].setValue(pastedData[i]);
      }
      // Focus last input
      const lastInput = document.querySelector('#digit-5') as HTMLInputElement;
      if (lastInput) {
        lastInput.focus();
      }
    }
  }

  clearAllDigits(): void {
    this.digitControls.forEach(control => control.setValue(''));
    // Focus first input
    const firstInput = document.querySelector('#digit-0') as HTMLInputElement;
    if (firstInput) {
      firstInput.focus();
    }
  }

  async onVerify2FA($event: MouseEvent) {
    if (!this.otpForm.valid || this.isVerifying || this.isBlocked) {
      return;
    }

    this.errorMessage = '';
    this.isVerifying = true;
    const digits = this.digitControls.map(control => control.value).join('');

    this.basiqService.verify2FaCodeToIndividualFn({...this.data, code: digits}).pipe(
      finalize(() => {
        this.isVerifying = false;
      })
    ).subscribe({
      next: (r) => {
        if (r?.status) {
          // Success - close dialog
          this.dialogRef.close({
            verified: true,
            code: digits
          });
          this.toastService.success('2FA verification successful');
        } else {
          // Failed verification - show error but keep dialog open
          this.errorMessage = r.message || 'Invalid code. Please try again.';
          this.clearAllDigits();
        }
      },
      error: (error: any) => {
        // Handle specific error messages from API
        const errorMsg = error.error?.message || 'An error occurred while verifying the code';
        this.errorMessage = errorMsg;
        
        // Check if user is blocked
        if (errorMsg.toLowerCase().includes('blocked') || errorMsg.toLowerCase().includes('maximum attempts')) {
          this.isBlocked = true;
          this.otpForm.disable();
        }

        this.clearAllDigits();
      }
    });
  }

  onResendCode() {
    if (this.isResending || this.resendCooldown > 0) {
      return;
    }

    this.isResending = true;
    this.errorMessage = '';

    this.basiqService.send2FaCodeToBusinessUserFn(this.data).pipe(
      finalize(() => {
        this.isResending = false;
      })
    ).subscribe({
      next: (response) => {
        if (response.status) {
          this.toastService.success(`New verification code sent via ${this.otpDeliveryMethod}`);
          this.clearAllDigits();
          this.otpForm.enable();
          this.isBlocked = false;
          
          // Start cooldown timer (60 seconds)
          this.startResendCooldown(60);
        } else {
          this.errorMessage = response.message || 'Failed to send verification code';
        }
      },
      error: (error: any) => {
        const errorMsg = error.error?.message || 'Failed to send verification code';
        this.errorMessage = errorMsg;
        
        // Parse rate limit message if present
        const waitMatch = errorMsg.match(/wait (\d+) seconds/i);
        if (waitMatch) {
          const seconds = parseInt(waitMatch[1], 10);
          this.startResendCooldown(seconds);
        }
        
        // Check if blocked
        if (errorMsg.toLowerCase().includes('blocked') || errorMsg.toLowerCase().includes('too many code requests')) {
          this.isBlocked = true;
        }
      }
    });
  }

  private startResendCooldown(seconds: number) {
    this.resendCooldown = seconds;
    
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }
    
    this.cooldownSubscription = interval(1000).subscribe(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.cooldownSubscription?.unsubscribe();
      }
    });
  }

  onCancel() {
    this.dialogRef.close({
      verified: false
    });
  }

  get canResend(): boolean {
    return !this.isResending && this.resendCooldown === 0 && !this.isBlocked;
  }

  get resendButtonText(): string {
    if (this.resendCooldown > 0) {
      return `Resend code (${this.resendCooldown}s)`;
    }
    return 'Resend code';
  }
}